import React, { useRef, useState, useEffect } from "react"
import { ArrowDown, ArrowUp } from "lucide-react"
import { LayerItem, ItemSummary } from "../../services/types"
import { capitalizeWords } from "../../utils/helpers"
import TableSkeleton from "../skeletons/TableSkeleton"
import { useOrgContext } from "../../context/OrgIdContext"
import { fetchProgramDetailDashboardView } from "../../utils/dashboardUtils"

type SortColumn = "name" | "category" | "value";
type SortDirection = "asc" | "desc";

interface ItemsTableProps {
  items: LayerItem[];
  layerId: number | null;
  dashboardView: string;
  summary: ItemSummary | null;
  loading: boolean;
  selectedCategory: string | null;
  expandedSection?: "metrics" | "layerPanel" | null;
}

const ItemsTable: React.FC<ItemsTableProps> = ({
  items,
  layerId,
  dashboardView,
  summary,
  loading,
  selectedCategory,
  expandedSection
}) => {
    const origin = "*";
    const prevViewRef = useRef<string | null>(null);
    const tableRef = useRef<HTMLDivElement>(null);
    const { locationId } = useOrgContext();
    const [showDetailsId, setShowDetailsId] = useState<number | null>(null);
    const [visibleCount, setVisibleCount] = useState(10);
    
    // Sorting state
    const [sortColumn, setSortColumn] = useState<SortColumn>("name");
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

    // Click handlers
    const handleClick = async (item: LayerItem) => {
        setShowDetailsId(item.id);

        // Fetch the program detail dashboard view
        const view = await fetchProgramDetailDashboardView(locationId);

        if (layerId !== null) {
        window.parent.postMessage(
            {
            message: "showProgramDetailView",
            data: {
                layerId,
                id: item.id,
                view
            },
            },
            origin
        );

        console.log("Showing map item detail slideout modal...");
        };
    };

    const handleShowMapClick = (item: LayerItem, e: React.MouseEvent) => {
        e.stopPropagation();

        if (layerId !== null) {
            window.parent.postMessage(
                {
                    message: "showProgram",
                    data: {
                        id: item.id,
                        layerId,
                    },
                },
                origin
            );

            console.log("Program point message sent:", {
                id: item.id,
                layerId,
            });
        };
    };

    // Utility functions for filtering and sorting sites
    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    const filteredItems = selectedCategory 
        ? items.filter(item => item.category?.toLowerCase() === selectedCategory.toLowerCase())
        : items;

    const sortedItems = [...filteredItems].sort((a, b) => {
        let aValue: string | number = a[sortColumn] ?? "";
        let bValue: string | number = b[sortColumn] ?? "";

        const isAEmpty = aValue === null || aValue === undefined || aValue === "";
        const isBEmpty = bValue === null || bValue === undefined || bValue === "";

        if (isAEmpty && !isBEmpty) return 1;
        if (!isAEmpty && isBEmpty) return -1;
        if (isAEmpty && isBEmpty) return 0;

        // Normalize for consistent comparison
        if (typeof aValue === "string") aValue = aValue.toLowerCase();
        if (typeof bValue === "string") bValue = bValue.toLowerCase();

        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
    });

    const visibleItems = sortedItems.slice(0, visibleCount);
    const hasMore = visibleItems.length < sortedItems.length;

    const getCategoryColor = (category: string) => {
        switch (category?.toLowerCase()) {
            case "near term":
                return "bg-[#A3C9A8] text-[#1B4332]";
            case "mid term":
                return "bg-[#A0BFE0] text-[#102A43]";
            case "near term - unlikely to convert":
                return "bg-[#FFE29A] text-[#5F370E]";
            case "long term":
                return "bg-[#CA9DFF] text-[#49276F]"
            default:
                return "bg-gray-600 text-white";
        };
    };

    // Effects
    useEffect(() => {
        if (prevViewRef.current !== null && prevViewRef.current !== dashboardView) {
            setShowDetailsId(null);
        };
        prevViewRef.current = dashboardView;
    }, [dashboardView]);

    // Reset visibleCount when relevant inputs change
    useEffect(() => {
    setVisibleCount(10);
    }, [items, selectedCategory, sortColumn, sortDirection]);

    // Scroll event listener to load more on scroll near bottom
    useEffect(() => {
    const handleScroll = () => {
        if (!tableRef.current || !hasMore) return;

        const { scrollTop, scrollHeight, clientHeight } = tableRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 50) {
        setVisibleCount((prev) => prev + 10);
        }
    };

    const node = tableRef.current;
    node?.addEventListener("scroll", handleScroll);
    return () => node?.removeEventListener("scroll", handleScroll);
    }, [hasMore]);

    // Auto-load more if container isn't scrollable but more items exist
    useEffect(() => {
    const node = tableRef.current;
    if (!node || !hasMore) return;

    if (node.scrollHeight <= node.clientHeight) {
        // Not scrollable, so load more items (up to max available)
        setVisibleCount((prev) => Math.min(prev + 10, sortedItems.length));
    }
    }, [visibleCount, sortedItems.length, hasMore, expandedSection]);

    if (loading) return <TableSkeleton/>

    return (
        <div className="max-w-6xl mx-auto">
            {visibleItems.length > 0 && (
            <div className="border rounded-md bg-white shadow-md">
                <div className="overflow-x-auto">
                    <table className="w-full table-fixed text-xs">
                        <colgroup>
                            <col style={{ width: "50%" }} />
                            <col style={{ width: "30%" }} />
                            <col style={{ width: "20%" }} />
                        </colgroup>
                        <thead className="bg-gray-100 text-left">
                            <tr>
                                <th
                                className="px-2 py-2 cursor-pointer group"
                                onClick={() => handleSort("name")}
                                >
                                Name{" "}
                                {sortColumn === "name" ? (
                                    sortDirection === "asc" ? (
                                    <ArrowUp strokeWidth={3} className="inline w-3 h-3 text-black group-hover:text-gray-600" />
                                    ) : (
                                    <ArrowDown strokeWidth={3} className="inline w-3 h-3 text-black group-hover:text-gray-600" />
                                    )
                                ) : (
                                    <ArrowUp strokeWidth={2} className="inline w-3 h-3 text-gray-400 group-hover:text-gray-600" />
                                )}
                                </th>
                                <th
                                className="px-2 py-2 cursor-pointer group"
                                onClick={() => handleSort("category")}
                                >
                                Category{" "}
                                {sortColumn === "category" ? (
                                    sortDirection === "asc" ? (
                                    <ArrowUp strokeWidth={3} className="inline w-3 h-3 text-black group-hover:text-gray-600" />
                                    ) : (
                                    <ArrowDown strokeWidth={3} className="inline w-3 h-3 text-black group-hover:text-gray-600" />
                                    )
                                ) : (
                                    <ArrowUp strokeWidth={2} className="inline w-3 h-3 text-gray-400 group-hover:text-gray-600" />
                                )}
                                </th>
                                <th
                                className="px-2 py-2 hide-vs cursor-pointer group"
                                onClick={() => handleSort("value")}
                                >
                                Size{" "}
                                {sortColumn === "value" ? (
                                    sortDirection === "asc" ? (
                                    <ArrowUp strokeWidth={3} className="inline w-3 h-3 text-black group-hover:text-gray-600" />
                                    ) : (
                                    <ArrowDown strokeWidth={3} className="inline w-3 h-3 text-black group-hover:text-gray-600" />
                                    )
                                ) : (
                                    <ArrowUp strokeWidth={2} className="inline w-3 h-3 text-gray-400 group-hover:text-gray-600" />
                                )}
                                </th>
                            </tr>
                        </thead>
                    </table>
                    {/* This is the scrollable tbody container */}
                    <div
                        ref={tableRef}
                        className={`overflow-y-auto ${expandedSection === 'metrics' ? 'max-h-[310px]' : 'max-h-[540px]'} `}
                    >
                        <table className="w-full table-fixed text-xs">
                            <colgroup>
                                <col style={{ width: "50%" }} />
                                <col style={{ width: "30%" }} />
                                <col style={{ width: "20%" }} />
                            </colgroup>
                            <tbody>
                                {visibleItems.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-start px-4 py-8 text-gray-400">
                                    No items available.
                                    </td>
                                </tr>
                                ) : (
                                visibleItems.map((item) => (
                                    <tr
                                    key={item.id}
                                    className={`border-t hover:bg-gray-50 cursor-pointer ${
                                        showDetailsId === item.id ? "bg-blue-50" : ""
                                    }`}
                                    onClick={(e) => {
                                        handleClick(item);
                                        handleShowMapClick(item, e);
                                    }}
                                    >
                                    <td className="p-2 font-medium">
                                        <span>{capitalizeWords(item.name || "TBD")}</span>
                                    </td>
                                    <td className="p-2">
                                        <span
                                        className={`p-0.5 text-[10px] font-semibold rounded text-center whitespace-pre-wrap break-words flex items-center justify-center ${getCategoryColor(item.category)}`}
                                        title={item.category || "—"}
                                        >
                                        {item.category || "—"}
                                        </span>
                                    </td>
                                    <td className="p-2 hide-vs">
                                        {item.value !== null ? (
                                        <div className="flex flex-col justify-end text-right">
                                            <span className="font-semibold">{item.value}</span>
                                            <span className="text-[10px] leading-none">{summary?.unit_descriptor}</span>
                                        </div>
                                        ) : (
                                        <span className="text-gray-400">—</span>
                                        )}
                                    </td>
                                    </tr>
                                ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>  
            </div>
            )}
        </div>
    )
}

export default ItemsTable;